import React from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Icon,
  useBreakpointValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { MdLocationOn, MdVideoCall } from 'react-icons/md';

const MotionBox = motion(Box);

/**
 * Workshop Schedule Section
 * Displays in-class and online options with location/zoom details
 */
const WorkshopSchedule = ({ workshop, location, onlineUrl }) => {
  const gridCols = useBreakpointValue({ base: 1, md: 2 });

  if (!workshop) return null;

  const { schedule, online } = workshop;

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <Box py={{ base: 16, md: 20 }} bg="gray.50">
      <Container maxW="container.lg">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          mb={{ base: 12, md: 16 }}
        >
          <VStack align="center" spacing={3} textAlign="center">
            <Heading as="h2" size="xl" fontWeight="900" color="gray.900">
              How to Join
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              Choose your preferred way to attend the workshop
            </Text>
          </VStack>
        </MotionBox>

        {/* Options Grid */}
        <SimpleGrid columns={gridCols} spacing={8}>
          {/* In-Class Option */}
          {location && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
            >
              <Box
                bg="white"
                p={8}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="purple.600"
                h="full"
              >
                <HStack spacing={4} mb={6}>
                  <Icon as={MdLocationOn} w={6} h={6} color="purple.600" />
                  <Heading as="h3" size="md" fontWeight="bold">
                    In-Class
                  </Heading>
                </HStack>

                <VStack align="start" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" color="gray.600" fontSize="sm">
                      Location
                    </Text>
                    <Text color="gray.900" mt={1}>
                      {location.name}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" color="gray.600" fontSize="sm">
                      Address
                    </Text>
                    <Text color="gray.900" mt={1} fontSize="sm" lineHeight="1.6">
                      {location.address}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" color="gray.600" fontSize="sm">
                      Time
                    </Text>
                    <Text color="gray.900" mt={1}>
                      {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)} {schedule.timezone}
                    </Text>
                  </Box>

                  {location.url && (
                    <Box mt={4} w="full">
                      <Text
                        as="a"
                        href={location.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="purple.600"
                        fontWeight="bold"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        View on Google Maps →
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Box>
            </MotionBox>
          )}

          {/* Online Option */}
          {online && (
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Box
                bg="white"
                p={8}
                borderRadius="lg"
                borderLeft="4px solid"
                borderColor="green.600"
                h="full"
              >
                <HStack spacing={4} mb={6}>
                  <Icon as={MdVideoCall} w={6} h={6} color="green.600" />
                  <Heading as="h3" size="md" fontWeight="bold">
                    Online
                  </Heading>
                </HStack>

                <VStack align="start" spacing={4}>
                  <Box>
                    <Text fontWeight="bold" color="gray.600" fontSize="sm">
                      Time
                    </Text>
                    <Text color="gray.900" mt={1}>
                      {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)} {schedule.timezone}
                    </Text>
                  </Box>

                  <Box>
                    <Text fontWeight="bold" color="gray.600" fontSize="sm">
                      Join Link
                    </Text>
                    <Text color="gray.600" mt={2} fontSize="sm">
                      You'll receive the Zoho meeting link in your confirmation email
                    </Text>
                  </Box>

                  <Box bg="blue.50" p={4} borderRadius="md" w="full" mt={4}>
                    <Text fontSize="sm" color="blue.700">
                      💡 Tip: Join 5 minutes early for a smooth experience
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </MotionBox>
          )}
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default WorkshopSchedule;
